<template>
  <div class="line-item-view">
    <div class="line-item-view-row flex">
      <KeyValuePair
        :label="t('common.description')"
        :value="lineItem.description"
        class="w-4 border-right-1"
      />
      <KeyValuePair
        :label="t('lineItems.handling')"
        :value="
          formatQtyWithUnit(lineItem.quantity, lineItem.handlingUnit?.name)
        "
        class="w-2 border-right-1"
      />
      <KeyValuePair
        :label="t('lineItems.packaging')"
        :value="
          formatQtyWithUnit(lineItem.packagingQty, lineItem.packagingUnit?.name)
        "
        class="w-2 border-right-1"
      />
      <KeyValuePair
        :label="t('lineItems.nmfc')"
        :value="lineItem.nmfcNumber ?? '-'"
        class="w-2 border-right-1"
      />
      <div class="w-2 flex justify-content-between">
        <KeyValuePair
          :label="t('lineItems.class')"
          :value="lineItem.freightClass"
        />
        <EllipsisMenu v-if="editable" :menu-items="menuItems" />
      </div>
    </div>

    <div class="line-item-view-row flex border-top-1">
      <KeyValuePair
        :label="t('common.lengthXWidthXHeight')"
        :value="dimsString"
        class="w-2 border-right-1"
      />
      <KeyValuePair
        :label="t('lineItems.weight')"
        :value="
          translateNumberedEntity(
            lineItem.weight,
            `uoms.weight.abbreviations.${lineItem.weightUom}`,
          )
        "
        class="w-2 border-right-1"
      />
      <KeyValuePair
        :label="t('lineItems.turnable')"
        :value="renderBoolean(lineItem.turnable)"
        class="w-2 border-right-1"
      />
      <KeyValuePair
        :label="t('lineItems.stackable')"
        :value="renderBoolean(lineItem.stackable)"
        class="w-2 border-right-1"
      />
      <KeyValuePair
        :label="t('lineItems.hazardous')"
        :value="renderBoolean(lineItem.hazmatData.hazmat)"
        class="w-2 border-right-1"
      />
      <KeyValuePair
        :label="t('temperatureSettings.refrigerated')"
        :value="renderBoolean(lineItem.temperatureSettings.needsRefrigeration)"
        class="w-2"
      />
    </div>
    <div
      v-if="lineItem.hazmatData.hazmat"
      class="line-item-view-row flex border-top-1"
    >
      <KeyValuePair
        :label="t('lineItems.hazardDescription')"
        :value="lineItem.hazmatData.hazardDescription"
        class="w-6 border-right-1"
      />
      <KeyValuePair
        :label="t('lineItems.unOrNaNumber')"
        :value="lineItem.hazmatData.unNumber"
        class="w-2 border-right-1"
      />
      <KeyValuePair
        :label="t('lineItems.hazardClass')"
        :value="hazardClass?.name"
        class="w-2 border-right-1"
      />
      <KeyValuePair
        :label="t('lineItems.packingGroup')"
        :value="translateEnum(lineItem.hazmatData.packingGroup, 'PackingGroup')"
        class="w-2"
      />
    </div>
    <div
      v-if="lineItem.temperatureSettings.needsRefrigeration"
      class="line-item-view-row flex border-top-1"
    >
      <KeyValuePair
        :label="t('temperatureSettings.minTemp')"
        :value="
          formatQtyWithUnit(
            lineItem.temperatureSettings.minTemp,
            temperatureUnit,
          )
        "
        class="w-2 border-right-1"
      />
      <KeyValuePair
        :label="t('temperatureSettings.maxTemp')"
        :value="
          formatQtyWithUnit(
            lineItem.temperatureSettings.maxTemp,
            temperatureUnit,
          )
        "
        class="w-2"
      />
    </div>

    <div class="line-item-view-row flex border-top-1 flex-wrap">
      <KeyValuePair
        :label="t('lineItems.productNumber')"
        :value="lineItem.productNumber"
        :class="{
          'border-bottom-1': hasRefNumBelow(1),
        }"
        class="w-2"
      >
      </KeyValuePair>
      <KeyValuePair
        :label="
          t('common.paramValues', {
            param: t('orders.orders'),
          })
        "
        :value="renderMoney(lineItem.valueOfGoods)"
        class="w-2"
      >
      </KeyValuePair>
      <!-- hasRefNumBelow depends on the 'w-2' class -->
      <KeyValuePair
        v-for="(referenceNum, index) in lineItem.referenceNumbers"
        :key="referenceNum.typeId"
        :value="referenceNum.value"
        :label="referenceNum.typeName"
        :class="{
          'border-bottom-1': hasRefNumBelow(index),
        }"
        class="w-2 border-left-1"
      />
    </div>

    <div
      v-if="showStops && (pickup || dropoff)"
      class="line-item-view-row flex border-top-1"
    >
      <KeyValuePair
        :label="t('shipments.pickups')"
        :value="pickup"
        class="w-6 border-right-1"
      />
      <KeyValuePair
        :label="t('shipments.dropoffs')"
        :value="dropoff"
        class="w-6"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  renderBoolean,
  renderMoney,
  translateEnum,
  translateNumberedEntity,
} from '@rygen/rygen-ui/i18n';
import { HazardClass } from '@rygen/rygen-ui/model';

import { LineItem, Shipment } from '@/model';
import { ConsolidatedShipment } from '@/model/consolidation';
import { useParentEntityStore } from '@/stores/ParentEntity';

import EllipsisMenu from '@rygen/rygen-ui/components/ui/EllipsisMenu.vue';
import KeyValuePair from '@rygen/rygen-ui/components/ui/KeyValuePair.vue';

const props = defineProps({
  lineItem: {
    type: LineItem,
    default: () => new LineItem(),
  },
  removable: {
    type: Boolean,
    default: () => false,
  },
  editable: {
    type: Boolean,
    default: () => false,
  },
  showStops: {
    type: Boolean,
    default: () => false,
  },
});
const { lineItem, removable, editable, showStops } = toRefs(props);
const emit = defineEmits<{
  (event: 'duplicate', lineItem: LineItem): void;
  (event: 'edit', lineItem: LineItem): void;
  (event: 'remove', newVal: LineItem): void;
}>();

const { t } = useI18n();
const { parentEntity } = useParentEntityStore();

const param = t('lineItems.lineItems');

// TODO: Revisit this when we decide how to handle handling/packaging units customizations/translations
const formatQtyWithUnit = (qty?: number, uom = ''): string => {
  return qty !== undefined ? `${qty} ${uom}` : '-';
};

const menuItems = computed(() => {
  return [
    {
      label: t('common.editParam', { param }),
      icon: 'pi pi-fw pi-pencil',
      command: () => emit('edit', lineItem.value),
    },
    {
      label: t('common.duplicateParam', { param }),
      icon: 'pi pi-fw pi-copy',
      command: () => emit('duplicate', lineItem.value),
    },
    {
      label: t('common.deleteParam', { param }),
      disabled: !removable.value,
      class: 'text-danger',
      icon: 'pi pi-fw pi-trash',
      command: () => emit('remove', lineItem.value),
    },
  ];
});

const lengthString = computed(() => {
  return translateNumberedEntity(
    lineItem.value.lengthDim,
    `uoms.length.abbreviations.${lineItem.value.dimensionalUom}`,
  );
});

const widthString = computed(() => {
  return translateNumberedEntity(
    lineItem.value.widthDim,
    `uoms.length.abbreviations.${lineItem.value.dimensionalUom}`,
  );
});

const heightString = computed(() => {
  return translateNumberedEntity(
    lineItem.value.heightDim,
    `uoms.length.abbreviations.${lineItem.value.dimensionalUom}`,
  );
});

const dimsString = computed(() => {
  return `${lengthString.value} x ${widthString.value} x ${heightString.value}`;
});

const hazardClass = computed(() => {
  return HazardClass.ALL_CLASSES.find(
    (h) => h.code === lineItem.value.hazmatData.hazardClass,
  );
});

const temperatureUnit = computed(() =>
  t(
    `uoms.temperature.abbreviations.${lineItem.value.temperatureSettings.temperatureUom}`,
  ),
);

const pickupStop = computed(() => {
  const pickupStopSequenceNumber = lineItem.value.pickupStopSequenceNumber;

  if (
    parentEntity.value instanceof Shipment ||
    parentEntity.value instanceof ConsolidatedShipment
  ) {
    return parentEntity.value.stops.find(
      (s) => s.sequenceNumber === pickupStopSequenceNumber,
    );
  }

  return undefined;
});
const pickup = computed(
  () =>
    [
      pickupStop.value?.location?.locationName,
      pickupStop.value?.location?.cityState,
    ]
      .map((x) => x?.trim())
      .filter((x) => !!x)
      .join(' • ') || '-',
);

const dropoffStop = computed(() => {
  const dropOffStopSequenceNumber = lineItem.value.dropOffStopSequenceNumber;

  if (
    parentEntity.value instanceof Shipment ||
    parentEntity.value instanceof ConsolidatedShipment
  ) {
    return parentEntity.value.stops.find(
      (s) => s.sequenceNumber === dropOffStopSequenceNumber,
    );
  }

  return undefined;
});
const dropoff = computed(
  () =>
    [
      dropoffStop.value?.location?.locationName,
      dropoffStop.value?.location?.cityState,
    ]
      .map((x) => x?.trim())
      .filter((x) => !!x)
      .join(' • ') || '-',
);

const hasRefNumBelow = (index: number) => {
  // Class w-2 on the ref nums means 6 items per row
  const numPerRow = 6;

  const staticCells = 1;
  const effectiveIndex = index + staticCells;

  const row = Math.trunc(effectiveIndex / numPerRow) + 1;
  const numRows = Math.ceil(
    (lineItem.value.referenceNumbers.length + staticCells) / numPerRow,
  );

  return row < numRows;
};
</script>

<style>
.line-item-view {
  --line-item-view-row-background-color: white;

  .line-item-view-row {
    &:not(:first-of-type) {
      --line-item-view-row-background-color: var(--p-surface-50);
    }

    background-color: var(--line-item-view-row-background-color);
    border-color: var(--divider-color);

    .key-value-pair {
      padding: var(--sp-3);
      border-color: var(--divider-color);

      .key-value-pair-label {
        text-transform: uppercase;
      }

      .key-value-pair-value {
        color: var(--p-gray-600);
        display: block;
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
        /** overflow doesn't work if there's not an explicit width set */
        width: 100%;
      }
    }

    .ellipsis-menu {
      padding: var(--sp-3);
    }
  }
}
</style>
